import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import "./Projects.css";
import { useProjects } from "../../hooks/projects/useProjects";
import { useAddProject } from "../../hooks/projects/useAddProject";
import { useUpdateProject } from "../../hooks/projects/useUpdateProject";
import { useDeleteProject } from "../../hooks/projects/useDeleteProject";
import { useOrganizations } from "../../hooks/org/useOrganizations";
import { useProject } from "../../hooks/projects/useProject";
import { useProjectAssignment } from "../../hooks/projects/useProjectAssignment";
import { useUpdateProjectAssignment } from "../../hooks/projects/useUpdateProjectAssignment";
import { useAuth } from '../../hooks/auth/useAuth';
import {
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { Folder, Play, Map, Users, Edit, Eye, Search, Plus, XCircle, UserPlus, Trash, Download } from "lucide-react";
import { useUsers } from '../../hooks/user/useUsers';
import { useMemo } from "react";
import FormDialog from "../../assets/FormDialog";
import AssignmentForm from "./AssignmentForm";

export default function Projects() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const currentUserRole = currentUser?.role;
  const canAssign = currentUserRole === "client_admin" || currentUserRole === "project_admin";
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [vendorFilter, setVendorFilter] = useState("All Vendors");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const { assignUserToProject, assignLoading } = useProjectAssignment();
  const { organizations, loading: orgLoading, error: orgError } = useOrganizations(true);
  const { projects, totals, loading, error, refetch } = useProjects({
    page,
    pageSize,
    search: activeSearch,
    status: statusFilter,
    vendor: vendorFilter,
  });

  const { deleteProject, deleteLoading } = useDeleteProject();
  const { updateAssignment, updateassignLoading } = useUpdateProjectAssignment();

  // Search logic
  const handleApplySearch = () => {
    const trimmed = searchQuery.trim();
    setActiveSearch(trimmed);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearch("");
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApplySearch();
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === "" && activeSearch !== "") {
      handleClearSearch();
    }
  }, [searchQuery]);

  const { addProject, loading: addLoading } = useAddProject();
  const { updateProject, loading: updateLoading } = useUpdateProject();

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const projectAdminFilters = useMemo(() => ({
      search: '',
      group__name: 'project_admin',
      is_active: true
    }), []);

    const {
      users: projectAdmins,
      loading: projectAdminLoading,
      error: projectAdminError
    } = useUsers(
      projectAdminFilters,
      1,
      1000,
      currentUser,
      true
    );

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    location_name: "",
    total_area: "",
    start_date: "",
    end_date: "",
    status: "planning",
    priority: "low",
    organization: "",
    project_admin: "",   
    latitude: "",
    longitude: "",
    kml_file: null,
    images: null,
    documents: null,
  });
  const canSelectProjectAdmin =
   currentUserRole === "client_admin";
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [projectToAssign, setProjectToAssign] = useState(null);
  const [assignFormData, setAssignFormData] = useState({
    user: '',
    access_level: 'read_write',
    start_date: '',
    end_date: '',
    remark: '',
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
 const handleOpenAssignDialog = (project) => {
  setProjectToAssign(project);

  const assignment = project?.surveyor_assignment;

  if (assignment) {
    setAssignFormData({
      id: assignment.id,
      user: assignment.user || assignment.user_id || "",
      access_level: assignment.access_level || "read_write",
      start_date: assignment.start_date || "",
      end_date: assignment.end_date || "",
      remark: assignment.remark || "",
    });
  } else {
    setAssignFormData({
      id: null,
      user: "",
      access_level: "read_write",
      start_date: "",
      end_date: "",
      remark: "",
    });
  }

  setIsAssignDialogOpen(true);
};

  const handleAssignFormChange = (e) => {
    const { name, value } = e.target;
    setAssignFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssignSubmit = async () => {
    if (!assignFormData.user) {
      toast.warn("Please select a user");
      return;
    }

    try {
      if (assignFormData.id) {
        await updateAssignment(assignFormData.id, assignFormData);
        toast.success("Surveyor updated successfully");
      } else {
        await assignUserToProject(projectToAssign.id, assignFormData);
        toast.success("Surveyor assigned successfully");
      }

      await refetch();
      setIsAssignDialogOpen(false);

    } catch (err) {
      console.error(err);
    }
  };

 const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      // convert FileList to Array
      setFormData((prev) => ({ ...prev, [name]: Array.from(files) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

 const buildPayload = (data) => {
  const payload = new FormData();
  
  // Handle coordinates
  const lat = parseFloat(data.latitude);
  const lon = parseFloat(data.longitude);
  if (!isNaN(lat) && !isNaN(lon)) {
    payload.append("location_coordinates", JSON.stringify({
      type: "Point",
      coordinates: [lon, lat],
    }));
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value == null || value === "" || key === "latitude" || key === "longitude") return;

    if ((key === "images" || key === "documents") && Array.isArray(value)) {
      // Only append actual File objects
      value.forEach((file) => {
        if (file instanceof File) {
          payload.append(key, file);
        }
      });
    } else if (value instanceof File) {
      payload.append(key, value);
    } else {
      payload.append(key, value);
    }
  });

  return payload;
};

  const handleSubmit = async (data) => {
    const payload = buildPayload(data);
    try {
      await addProject(payload);
      await refetch();
      setOpenDialog(false);
      setFormData({
        name: "", code: "", description: "", location_name: "", total_area: "",
        start_date: "", end_date: "", status: "planning", priority: "low",
        organization: "", latitude: "", longitude: "", kml_file: null,
        images: null, documents: null,
      });
    } catch (err) {
      console.error("Failed to add project:", err);
    }
  };

  const handleUpdateSubmit = async (data) => {
    const payload = buildPayload(data);
    try {
      await updateProject(selectedProjectId, payload);
      await refetch();
      setIsEditDialogOpen(false);
      setSelectedProjectId(null);
    } catch (err) {
      console.error("Failed to update project:", err);
    }
  };

  const handleEdit = (project) => {
    setSelectedProjectId(project.id);
    setIsEditDialogOpen(true);
  };

  const handleView = (project) => {
    setSelectedProjectId(project.id);
    setIsViewDialogOpen(true);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    try {
      await deleteProject(projectToDelete.id);
      toast.success("Project deleted successfully");
      await refetch();
    } catch (err) {
      toast.error("Failed to delete project");
    } finally {
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: { label: "Active", class: "status-active" },
      planning: { label: "Planning", class: "status-planning" },
      completed: { label: "Completed", class: "status-completed" },
      on_hold: { label: "On Hold", class: "status-on-hold" },
      cancelled: { label: "Cancelled", class: "status-cancelled" },
      archived: { label: "Archived", class: "status-archived" },
    };
    return configs[status] || configs.planning;
  };

  const addProjectFields = [
    { name: "name", label: "Project Name", type: "text", required: true, gridWidth: 12, group: "Basic Info" },
    { name: "code", label: "Project Code", type: "text", required: true, gridWidth: 12, group: "Basic Info" },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Basic Info",
      options: [
        { value: "planning", label: "Planning" },
        { value: "active", label: "Active" },
        { value: "on_hold", label: "On Hold" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "archived", label: "Archived" },
      ],
    },
    {
      name: "priority",
      label: "Priority",
      type: "select",
      required: true,
      gridWidth: 6,
      group: "Basic Info",
      options: [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
        { value: "critical", label: "Critical" },
      ],
    },
    { name: "description", label: "Description", type: "multiline", required: true, rows: 3, gridWidth: 12, group: "Details" },
    {
      name: "organization",
      label: "Organization",
      type: "select",
      required: true,
      gridWidth: 12,
      group: "Details",
      options: orgLoading
        ? [{ value: "", label: "Loading..." }]
        : orgError
          ? [{ value: "", label: "Error loading organizations" }]
          : organizations.map((org) => ({ value: org.id, label: org.name })),
    },
    { name: "location_name", label: "Location Name", type: "text", required: true, gridWidth: 12, group: "Location" },
    {
      name: "latitude",
      label: "Latitude",
      type: "number",
      required: true,
      gridWidth: 6,
      group: "Location",
      placeholder: "e.g. 19.0760",
      validate: (v) => {
        if (!v) return "Required";
        const num = parseFloat(v);
        if (isNaN(num)) return "Must be a valid number";
        if (num < -90 || num > 90) return "Latitude must be between -90 and 90";
        return "";
      },
    },
    {
      name: "longitude",
      label: "Longitude",
      type: "number",
      required: true,
      gridWidth: 6,
      group: "Location",
      placeholder: "e.g. 72.8777",
      validate: (v) => {
        if (!v) return "Required";
        const num = parseFloat(v);
        if (isNaN(num)) return "Must be a valid number";
        if (num < -180 || num > 180) return "Longitude must be between -180 and 180";
        return "";
      },
    },
    {
      name: "kml_file",
      label: "KML/KMZ File (Project Boundary)",
      type: "file",
      gridWidth: 12,
      group: "Location",
      inputProps: { accept: ".kml,.kmz" },
      description: "Optional: Upload KML/KMZ to define project boundary",
    },{
        name: "total_area",
        label: "Total Area (ha)",
        type: "number",
        gridWidth: 6,
        group: "Location",
        customRender: (value) => {
          if (!value) return <Typography color="text.secondary">Not measured</Typography>;
          
          const num = parseFloat(value);
          const display = isNaN(num) 
            ? value 
            : `${num.toFixed(2).replace(/\.?0+$/, "")} ha`;
            
          return <Typography fontWeight={500}>{display}</Typography>;
        },
        },
    { name: "start_date", label: "Start Date", type: "date", required: true, gridWidth: 6, group: "Dates", InputLabelProps: { shrink: true } },
    { name: "end_date", label: "End Date", type: "date", gridWidth: 6, group: "Dates", InputLabelProps: { shrink: true } },
    { name: "images", label: "Project Images", type: "file", gridWidth: 6, group: "Files", inputProps: { accept: "image/*", multiple: true } },
    { name: "documents", label: "Documents", type: "file", gridWidth: 6, group: "Files", inputProps: { multiple: true } },
  ];
    if (canSelectProjectAdmin) {
    addProjectFields.push({
      name: "project_admin",
      label: "Project Manager",
      type: "select",
      required: true,
      gridWidth: 12,
      group: "Details",
      options: projectAdminLoading
        ? [{ value: "", label: "Loading..." }]
        : projectAdminError
          ? [{ value: "", label: "Error loading users" }]
          : projectAdmins.map((user) => ({
              value: user.id,
              label: user.full_name || user.profile?.full_name,
            })),
    });
  }

  const editProjectFields = addProjectFields.map(field => {
    if (field.name === "total_area") {
      return {
        ...field,
        type: "number",   // make editable
        placeholder: "e.g. 12.5",
        InputProps: { min: 0 },
        validate: (v) => {
          if (!v) return "Required";
          const num = parseFloat(v);
          if (isNaN(num) || num < 0) return "Must be a valid positive number";
          return "";
        },
      };
    }
    return field;
  });

  const viewProjectFields = addProjectFields.map((field) => {
    let updatedField = { ...field, required: false };
    if (field.name === "total_area") {
      return {
        ...field,                    // keeps type: "number"
        type: "number",              // override only for view
        customRender: (value) => { /* your nice display logic */ },
      };
    }
    if (field.name === "latitude" || field.name === "longitude") {
      updatedField.type = "text";
    }
    if (field.type === "select" || field.type === "file") {
      updatedField.type = "custom";
      updatedField.options = undefined;
    }

    // Custom rendering for images & documents in view mode
    if (field.name === "images" || field.name === "documents") {
      updatedField.customRender = (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return <Typography color="text.secondary">No files uploaded</Typography>;
        }

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            {value.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  maxWidth: 220,
                  textAlign: 'center',
                }}
              >
                {file.content_type?.startsWith('image/') ? (
                  <img
                    src={file.url}
                    alt={file.original_name || "Image"}
                    style={{
                      maxWidth: '180px',
                      maxHeight: '140px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      marginBottom: '8px',
                    }}
                    onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                  />
                ) : (
                  <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2">📄</Typography>
                  </Box>
                )}
                <Typography variant="body2" noWrap title={file.original_name || file.filename}>
                  {file.original_name || file.filename || "File"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
                <IconButton
                  size="small"
                  href={file.url}
                  target="_blank"
                  download
                  sx={{ mt: 0.5 }}
                >
                  <Download size={16} />
                </IconButton>
              </Box>
            ))}
          </Box>
        );
      };
    }

    return updatedField;
  });

  const getEditInitialValues = (project) => ({
    name: project?.name || "",
    code: project?.code || "",
    description: project?.description || "",
    location_name: project?.location_name || "",
    total_area: project.total_area || "",
    start_date: project?.start_date || "",
    end_date: project?.end_date || "",
    status: project?.status || "planning",
    priority: project?.priority || "low",
    project_admin: project?.project_admin || "",
    organization: project?.organization || "",
    latitude: project?.location_coordinates?.coordinates?.[1] ?? "",
    longitude: project?.location_coordinates?.coordinates?.[0] ?? "",
    kml_file: null,
    images: project?.images || [],
    documents: project?.documents || [],
  });

  const getViewDisplayValues = (proj) => {
    if (!proj) return {};

    const coords = proj.location_coordinates?.coordinates || [];
    let totalAreaDisplay = "Not measured";
    if (proj.total_area != null && proj.total_area !== "") {
      const areaNum = parseFloat(proj.total_area);
      if (!isNaN(areaNum)) {
        totalAreaDisplay = `${areaNum.toFixed(2).replace(/\.?0+$/, "")} ha`;
      } else {
        totalAreaDisplay = `${proj.total_area} ha`;
      }
    }

    return {
      name: proj.name || "Not set",
      code: proj.code || "—",
      description: proj.description || "No description available",
      location_name: proj.location_name || "Not specified",
      total_area: proj.total_area,
      start_date: proj.start_date || "—",
      end_date: proj.end_date || "—",
      expected_completion_date: proj.expected_completion_date || "—",
      status: getStatusConfig(proj.status)?.label || proj.status || "Unknown",
      priority: proj.priority
        ? proj.priority.charAt(0).toUpperCase() + proj.priority.slice(1)
        : "—",
      organization: proj.organization_name || "—",
      project_admin_name: proj.project_admin_name || "Not assigned",
      created_by_name: proj.created_by_name || "—",
      latitude: coords[1] != null ? coords[1].toFixed(6) : "—",
      longitude: coords[0] != null ? coords[0].toFixed(6) : "—",
      expected_tree_count: proj.expected_tree_count != null
        ? proj.expected_tree_count.toLocaleString()
        : "Not set",
      target_carbon_sequestration_kg: proj.target_carbon_sequestration_kg != null
        ? proj.target_carbon_sequestration_kg.toLocaleString()
        : "—",
      created_at: proj.created_at
        ? new Date(proj.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        : "—",
      updated_at: proj.updated_at
        ? new Date(proj.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        : "—",
      images: proj.images || [],
      documents: proj.documents || [],
      kml_file: proj.location_boundary
        ? `Defined (${proj.location_boundary.coordinates?.[0]?.length - 1 || 0} points)`
        : "No boundary uploaded",
    };
  };

  const {
    project: projectDetail,
    loading: detailLoading,
    error: detailError,
  } = useProject(isViewDialogOpen ? selectedProjectId : null);

  const {
    project: editProjectDetail,
    loading: editDetailLoading,
    error: editDetailError,
  } = useProject(isEditDialogOpen ? selectedProjectId : null);

  useEffect(() => {
    if (detailError && isViewDialogOpen) {
      const timer = setTimeout(() => {
        setIsViewDialogOpen(false);
        setSelectedProjectId(null);
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, [detailError, isViewDialogOpen]);

  useEffect(() => {
    if (editDetailError && isEditDialogOpen) {
      const timer = setTimeout(() => {
        setIsEditDialogOpen(false);
        setSelectedProjectId(null);
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, [editDetailError, isEditDialogOpen]);

  if (loading) return <div className="loading-state">Loading projects...</div>;
  if (error) return <div className="error-state">Error: {error}</div>;

  const totalPages = Math.ceil(totals.totalProjects / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totals.totalProjects);
  return (
    <div className="projects-page">
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">
            Manage and monitor all your reforestation projects
          </p>
        </div>
       
      </div>
      {/* Summary Cards */}
      <div className="summary-section">
        <div className="summary-card summary-card--blue">
          <div className="summary-card__icon"><Folder size={24} color="white" /></div>
          <div className="summary-card__content">
            <div className="summary-card__title">Total Projects</div>
            <div className="summary-card__value">{totals.totalProjects}</div>
          </div>
        </div>
        <div className="summary-card summary-card--green">
          <div className="summary-card__icon"><Play size={24} color="white" /></div>
          <div className="summary-card__content">
            <div className="summary-card__title">Active Projects</div>
            <div className="summary-card__value">{totals.activeProjects}</div>
          </div>
        </div>
        <div className="summary-card summary-card--teal">
          <div className="summary-card__icon"><Map size={24} color="white" /></div>
          <div className="summary-card__content">
            <div className="summary-card__title">Total Area</div>
            <div className="summary-card__value">{totals.totalArea} <span className="summary-card__unit">hectares</span></div>
          </div>
        </div>
        <div className="summary-card summary-card--purple">
          <div className="summary-card__icon"><Users size={24} color="white" /></div>
          <div className="summary-card__content">
            <div className="summary-card__title">Active Vendors</div>
            <div className="summary-card__value">{totals.activeVendors}</div>
          </div>
        </div>
      </div>
      {/* Controls Section */}
      <div className="controls-section">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Name, status, or vendor"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={handleApplySearch}
                      edge="start"
                      sx={{ ml: -1 }}
                    >
                      <Search size={16} color="#94a3b8" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <XCircle size={16} color="#94a3b8" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "white",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <select
              name="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="All Status">All Status</option>
              <option value="Active">Active</option>
              <option value="Planning">Planning</option>
              <option value="Completed">Completed</option>
            </select>
          </Grid>
          {currentUserRole !== "project_admin" && (
              <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => setOpenDialog(true)}
                  className="add-user-btn"
                  disabled={addLoading}
                >
                  <Plus size={16} /> New Project
                </Button>
              </Grid>
            )}
        </Grid>
      </div>
      {/* Projects Table */}
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Status</th>
              <th>Location</th>
              <th>Owner</th>
              <th>Trees Planned</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const statusConfig = getStatusConfig(project.status);
              return (
                <tr key={project.id} className="table-row">
                  <td>
                    <div className="user-info">
                      {project.thumbnail ? (
                        <img
                          src={project.thumbnail}
                          alt={project.name}
                          className="project-thumbnail"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="user-avatar"
                        style={{
                          backgroundColor: "#3b82f6",
                          display: project.thumbnail ? 'none' : 'flex',
                        }}
                      >
                        {project.name?.[0] || "?"}
                      </div>
                      <div className="user-details">
                        <div className="user-name">{project.name}</div>
                        <div className="user-subtitle">ID: {project.pid}</div> {/* optional */}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${statusConfig.class}`}>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td>{project.location_name || "Not specified"}</td>
                  <td>{project.organization_name || "N/A"}</td>

                  <td className="projects-cell">{project.expected_tree_count?.toLocaleString() || "N/A"}</td>
                 
                  <td>
                    <div className="table-actions">

                      {/* Only Project Admin can assign Surveyor */}
                     {currentUserRole === "project_admin" && (
                        <IconButton
                          onClick={() => handleOpenAssignDialog(project)}
                          size="small"
                          className="action-btn"
                          title={
                            project.surveyor_assignment
                              ? "Change Surveyor"
                              : "Assign Surveyor"
                          }
                        >
                          {project.surveyor_assignment ? (
                            <Users size={20} color="#16a34a" />
                          ) : (
                            <UserPlus size={20} color="#6366f1" />
                          )}
                        </IconButton>
                      )}

                      <IconButton
                        onClick={() => handleEdit(project)}
                        size="small"
                        className="action-btn"
                      >
                        <Edit size={20} color="#10b981" />
                      </IconButton>

                      <IconButton
                        onClick={() => handleView(project)}
                        size="small"
                        className="action-btn"
                      >
                        <Eye size={20} color="#06b6d4" />
                      </IconButton>

                      <IconButton
                        onClick={() => handleDeleteClick(project)}
                        size="small"
                        className="action-btn"
                        title="Delete Project"
                        disabled={deleteLoading}
                      >
                        <Trash size={20} color="#ef4444" />
                      </IconButton>

                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {projects.length === 0 && !loading && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" className="empty-icon" fill="#6b7280">
              <path d="M12 12c2.21 0 4-1.79 4-4S14.21 4 12 4 8 5.79 8 8s1.79 4 4 4zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
            </svg>
            <h3>No projects found</h3>
            <p>No projects match your current filters. Try adjusting your search criteria.</p>
          </div>
        )}
      </div>
      {/* Pagination */}
      <div className="pagination-section">
        <div className="pagination-info">
          Showing {startItem} to {endItem} of {totals.totalProjects} projects
        </div>
        <div className="pagination-controls">
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="pagination-btn">
            Previous
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) pageNum = i + 1;
            else if (page <= 3) pageNum = i + 1;
            else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = page - 2 + i;
            return (
              <button
                key={pageNum}
                className={`page-number ${page === pageNum ? "active" : ""}`}
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button onClick={() => setPage(page + 1)} disabled={page >= totalPages} className="pagination-btn">
            Next
          </button>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="page-size-select">
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
          </select>
        </div>
      </div>
      {/* Add Project Dialog */}
      <FormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title="Add New Project"
        fields={addProjectFields}
        initialValues={formData}
        onSubmit={handleSubmit}
        submitLabel={addLoading ? "Saving..." : "Create"}
      />

      {/* Edit Project Dialog */}
      <FormDialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedProjectId(null);
        }}
        title={
          editDetailLoading
            ? "Loading project details..."
            : editDetailError
              ? "Error loading project"
              : `Edit Project: ${editProjectDetail?.name || ""}`
        }
        fields={editProjectFields}
        initialValues={getEditInitialValues(editProjectDetail || {})}
        onSubmit={handleUpdateSubmit}
        submitLabel={updateLoading ? "Saving..." : "Save Changes"}
        loading={editDetailLoading}
        errorMessage={editDetailError}
      />

      {/* View Project Dialog */}
      <FormDialog
        open={isViewDialogOpen}
        onClose={() => {
          setIsViewDialogOpen(false);
          setSelectedProjectId(null);
        }}
        title={
          detailLoading
            ? "Loading project details..."
            : detailError
              ? "Error loading project"
              : `Project: ${projectDetail?.name || "Details"}`
        }
        fields={viewProjectFields}
        initialValues={getViewDisplayValues(projectDetail)}
        viewMode={true}
        submitLabel="Close"
        cancelLabel="Close"
        loading={detailLoading}
        errorMessage={detailError}
      />
      {/* Assign Dialog */}
      <Dialog
        open={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign {currentUserRole === 'client_admin'
            ? 'Project Admin'
            : 'Surveyor'}
          {projectToAssign && ` to "${projectToAssign.name}"`}
        </DialogTitle>
        <DialogContent dividers>
          <AssignmentForm
            formData={assignFormData}
            onChange={handleAssignFormChange}
            disabled={assignLoading}
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAssignDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={assignLoading || !assignFormData.user}
          >
            {assignLoading
              ? assignFormData.id
                ? "Updating..."
                : "Assigning..."
              : assignFormData.id
                ? "Update Assignment"
                : "Assign User"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the project{" "}
            <strong>"{projectToDelete?.name || "this project"}"</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}